/**
 * Freezing a mech's **loadout** — the pilot's flat Range-type range bonus, and the weapon,
 * mount and system snapshots the mech adapter takes at deploy time.
 */

import type { Compendium } from "@lancer-vtt/compendium";
import type { CombatantBase, FrozenMount, FrozenSystem, PilotData, WeaponProfile, WeaponRange } from "@lancer-vtt/domain";
import { composedMounts, talentIntegratedGrants } from "@lancer-vtt/domain";
import { freezeTagRefs, freezeWeaponProfile, parseDamageVal, type RawWeapon } from "../weaponProfile";

/**
 * Resolve the pilot's flat **Range-type range bonus** off held core bonuses (Neurolink
 * Targeting). `cb_neurolink_targeting`'s upstream lancer-data row is a structured
 * `{ id: "range", range_types: ["Range"], val: 3 }` riding a core bonus's `bonuses`
 * array via `.passthrough()` — read it **structurally**, the same discipline
 * `collectPilotBonuses` (`compute.ts`) already uses for every other core-bonus row.
 *
 * This deliberately does NOT route through {@link applyBonus}/{@link makeBonusApplier}:
 * those implement overwrite/replace/additive stacking for a named **mech stat** id
 * (`hp`, `evasion`, …), but `range` here isn't a `ComputedMechStats` field at all — it's
 * a flat per-weapon-band adjustment `freezeWeapons` folds in post-hoc, at a different
 * layer (spawn-freeze, not `compute`). The {@link Bonus} type also carries no
 * `range_types` field (its stacking-flag contract doesn't need one), so this reads the
 * raw passthrough row directly rather than widening `Bonus`.
 *
 * **Filters to `range_types.includes("Range")`** — the RAW distinction that keeps a
 * Threat-only melee weapon untouched (Neurolink Targeting: "Your **ranged** weapons
 * gain +3 Range," never Threat). Sums `val` across every matching row on every held
 * core bonus, so a pilot holding more than one such bonus (homebrew content — core
 * data ships exactly one) stacks additively. `0` for a pilot holding none (the common
 * case) — {@link freezeWeapons} then applies no adjustment at all.
 */
export function resolvePilotRangeBonus(pilot: PilotData, compendium: Compendium): number {
  let total = 0;
  for (const bonusId of pilot.coreBonuses) {
    const cb = compendium.getById("core_bonus", bonusId) as
      | { bonuses?: { id?: unknown; range_types?: unknown; val?: unknown }[] }
      | undefined;
    for (const row of cb?.bonuses ?? []) {
      if (!row || row.id !== "range") continue;
      if (!Array.isArray(row.range_types) || !row.range_types.includes("Range")) continue;
      if (typeof row.val === "number" && Number.isFinite(row.val)) total += row.val;
      else if (typeof row.val === "string") {
        const n = parseInt(row.val, 10);
        if (Number.isFinite(n)) total += n;
      }
    }
  }
  return total;
}

/**
 * The pilot's summed **`drone_hp`** bonus (Drone Commander 1's `{ id: "drone_hp", val: 5 }`), read
 * structurally off each held talent's unlocked ranks (cumulative, 1..N, as `collectPilotBonuses`
 * reads them). `0` when none is held.
 */
export function resolvePilotDroneHpBonus(pilot: PilotData, compendium: Compendium): number {
  let total = 0;
  for (const [talentId, rank] of Object.entries(pilot.talents)) {
    const talent = compendium.getById("talent", talentId) as { ranks?: { bonuses?: { id?: unknown; val?: unknown }[] }[] } | undefined;
    for (const r of (talent?.ranks ?? []).slice(0, rank)) {
      for (const row of r.bonuses ?? []) {
        if (row?.id !== "drone_hp") continue;
        const n = typeof row.val === "number" ? row.val : typeof row.val === "string" ? parseInt(row.val, 10) : NaN;
        if (Number.isFinite(n)) total += n;
      }
    }
  }
  return total;
}

/** One weapon-scoped row of a held talent rank's `bonuses` (`weapon_types` names a weapon class, or `"improvised"`). */
export type TalentWeaponBonus = { id: string; val: unknown; weaponTypes: string[]; weaponSizes?: string[] };

/**
 * The weapon-scoped `bonuses` rows of the pilot's held talent ranks (cumulative 1..N): Hunter 2's
 * `thrown` 5 on Auxiliary Melee weapons, Brawler 2's `damage` 2d6+2 and `knockback` 2 on Improvised
 * Attacks. Read structurally, as {@link resolvePilotDroneHpBonus} reads `drone_hp`.
 */
export function resolveTalentWeaponBonuses(pilot: PilotData, compendium: Compendium): TalentWeaponBonus[] {
  const out: TalentWeaponBonus[] = [];
  for (const [talentId, rank] of Object.entries(pilot.talents)) {
    const talent = compendium.getById("talent", talentId) as { ranks?: { bonuses?: { id?: unknown; val?: unknown; weapon_types?: unknown; weapon_sizes?: unknown }[] }[] } | undefined;
    for (const r of (talent?.ranks ?? []).slice(0, rank)) {
      for (const row of r.bonuses ?? []) {
        if (typeof row?.id !== "string" || !Array.isArray(row.weapon_types)) continue;
        const sizes = Array.isArray(row.weapon_sizes) ? row.weapon_sizes.map(String) : undefined;
        out.push({ id: row.id, val: row.val, weaponTypes: row.weapon_types.map(String), ...(sizes ? { weaponSizes: sizes } : {}) });
      }
    }
  }
  return out;
}

/** One weapon-scoped `range` row of an equipped system or held core bonus (External Batteries' "+5 RANGE … +1 THREAT" on energy weapons). */
export type ScopedRangeBonus = { weaponTypes: string[]; damageTypes?: string[]; rangeTypes: string[]; val: number };

/**
 * The weapon-scoped `range` rows (`weapon_types` plus `range_types`, optionally `damage_types`) on
 * the mech's equipped systems and the pilot's held core bonuses, read structurally. An unscoped
 * Range row (Neurolink Targeting) stays {@link resolvePilotRangeBonus}'s.
 */
export function resolveScopedRangeBonuses(args: { mech: PilotData["mechs"][number]; pilot: PilotData; compendium: Compendium }): ScopedRangeBonus[] {
  const { mech, pilot, compendium } = args;
  const items = [
    ...(mech.loadouts[mech.activeLoadoutIndex]?.systems ?? []).map((id) => compendium.getById("system", id)),
    ...pilot.coreBonuses.map((id) => compendium.getById("core_bonus", id)),
  ] as ({ bonuses?: { id?: unknown; val?: unknown; weapon_types?: unknown; damage_types?: unknown; range_types?: unknown }[] } | undefined)[];
  const out: ScopedRangeBonus[] = [];
  for (const item of items) {
    for (const row of item?.bonuses ?? []) {
      if (row?.id !== "range" || !Array.isArray(row.weapon_types) || !Array.isArray(row.range_types)) continue;
      const val = Number(row.val);
      if (!Number.isFinite(val)) continue;
      out.push({ weaponTypes: row.weapon_types.map(String), rangeTypes: row.range_types.map((t) => String(t).toLowerCase()), val, ...(Array.isArray(row.damage_types) ? { damageTypes: row.damage_types.map((t) => String(t).toLowerCase()) } : {}) });
    }
  }
  return out;
}

/** `profile` (and its alternate profiles) with each matching scoped row adding to its bands of that kind. */
function applyScopedRangeBonuses(profile: WeaponProfile, rows: readonly ScopedRangeBonus[]): WeaponProfile {
  const mine = rows.filter((r) => profile.type !== undefined && r.weaponTypes.includes(profile.type) && (r.damageTypes === undefined || r.damageTypes.includes(profile.damage.type)));
  const range = mine.length === 0 || !profile.range ? profile.range : profile.range.map((band) => ({ ...band, val: band.val + mine.filter((r) => r.rangeTypes.includes(band.kind)).reduce((n, r) => n + r.val, 0) }));
  return {
    ...profile,
    ...(range ? { range } : {}),
    ...(profile.profiles ? { profiles: profile.profiles.map((p) => applyScopedRangeBonuses({ ...p, type: p.type ?? profile.type }, rows)) } : {}),
  };
}

/** `profile` with each matching `thrown` row raising its Thrown to the row's value (Hunter 2: "gain Thrown 5 … if they already have Thrown, it increases to Thrown 5"). */
function applyThrownBonuses(profile: WeaponProfile, rows: readonly TalentWeaponBonus[]): WeaponProfile {
  const thrown = rows
    .filter((r) => r.id === "thrown" && profile.type !== undefined && r.weaponTypes.includes(profile.type) && (r.weaponSizes === undefined || (profile.mount !== undefined && r.weaponSizes.includes(profile.mount))))
    .map((r) => Number(r.val))
    .filter(Number.isFinite);
  if (thrown.length === 0) return profile;
  const val = Math.max(...thrown, profile.tags?.find((t) => t.id === "tg_thrown")?.val ?? 0);
  return { ...profile, tags: [...(profile.tags ?? []).filter((t) => t.id !== "tg_thrown"), { id: "tg_thrown", val }] };
}

/**
 * What a talent rewrites on the Improvised Attack (Brawler 2's `damage` and `knockback` rows on
 * `"improvised"`), for `selectWeapon` to lay over the built-in profile; `undefined` when no held
 * rank touches it. The damage keeps the built-in Kinetic type.
 */
export function freezeImprovisedAttack(rows: readonly TalentWeaponBonus[]): CombatantBase["improvisedAttack"] {
  const mine = rows.filter((r) => r.weaponTypes.includes("improvised"));
  if (mine.length === 0) return undefined;
  const out: NonNullable<CombatantBase["improvisedAttack"]> = {};
  for (const r of mine) {
    if (r.id === "damage") out.damage = { ...parseDamageVal(r.val), type: "kinetic" };
    if (r.id === "knockback" && Number.isFinite(Number(r.val))) out.tags = [...(out.tags ?? []).filter((t) => t.id !== "tg_knockback"), { id: "tg_knockback", val: Number(r.val) }];
  }
  return out;
}

/**
 * Bump every **Range-type** band of a frozen {@link WeaponProfile} by `bonus` (Neurolink
 * Targeting) — `"threat"`/`"blast"`/`"line"`/`"cone"`/`"burst"` bands are
 * left untouched, so a melee weapon's Threat reach never moves. Recurses into `profiles`
 * (a multi-profile weapon's alternate firing modes) so an alt Range-type profile gets the
 * same bump — RAW's "your ranged weapons gain +3 Range" applies to the weapon, not to
 * whichever single firing mode happens to be selected. A `bonus` of `0` (the pilot holds no
 * such core bonus — the overwhelming common case) or a rangeless profile short-circuits to
 * the profile unchanged, so this is a no-op freeze-shape-wise until the bonus is actually held.
 */
function applyRangeBonus(profile: WeaponProfile, bonus: number): WeaponProfile {
  if (bonus === 0) return profile;
  const bump = (range: WeaponRange[]) => range.map((r) => (r.kind === "range" ? { ...r, val: r.val + bonus } : r));
  return {
    ...profile,
    ...(profile.range ? { range: bump(profile.range) } : {}),
    ...(profile.profiles ? { profiles: profile.profiles.map((p) => applyRangeBonus(p, bonus)) } : {}),
  };
}

/**
 * Freeze a {@link WeaponProfile} for every weapon equipped in the mech's **active
 * loadout**. Walks the active loadout's mounts/slots,
 * resolves each `weaponId` out of the compendium **once**, and parses its
 * damage/tags into a frozen profile. A slot with no weapon, or a weapon id that
 * doesn't resolve, is skipped (a half-equipped mount deploys with the weapons it
 * does have). Order: mount order, then slot order (the build order the resolver's
 * weapon lookup is indifferent to — it keys by id).
 *
 * **Instance ids**: each frozen entry also gets a
 * unique `instanceId` (distinct from the compendium `id` two mounts of the same
 * weapon type share) so combat can track "has THIS copy fired," not "has this weapon
 * TYPE fired." Two disjoint, never-colliding namespaces:
 *   - a loadout weapon → `${mountIndex}:${slotIndex}` (its position in the active
 *     loadout — stable across a spawn, unique per mount/slot);
 *   - a frame-integrated weapon → `integrated:${index}` (its position in
 *     `integratedIds` — these aren't mount/slot-indexed, they deploy outside the
 *     loadout entirely, so they get their own namespace prefix).
 *
 * **`rangeBonus`** (Neurolink Targeting): a resolved flat `+N` off
 * {@link resolvePilotRangeBonus}, folded onto every frozen profile's Range-type band(s)
 * via {@link applyRangeBonus} — threaded through as a plain number (not the pilot +
 * compendium) so this function's own job stays "freeze weapons," not "also resolve
 * core-bonus rows." Defaults to `0` (no adjustment).
 *
 * **`pilot`**:
 * walks {@link composedMounts} — the native mounts PLUS any held bonus mount tail (the
 * Improved Armament Flex mount) — instead of `mech.loadouts[...].mounts` directly, so a
 * weapon equipped into a bonus mount actually arms in combat. The composed array is the
 * SAME one {@link freezeMounts} below must walk, in the SAME order, so the two agree on
 * every `${mountIndex}:${slotIndex}` instance id (`freezeMounts`'s ids cross-reference
 * into this function's frozen `weapons[]`).
 *
 * Takes a single args object rather than four positionals (`CODE_STYLE.md`'s gated
 * `max-params-3`).
 */
type FreezeWeaponsArgs = {
  mech: PilotData["mechs"][number];
  pilot: PilotData;
  compendium: Compendium;
  integratedIds?: string[];
  rangeBonus?: number;
  /** Held talent ranks' weapon-scoped rows ({@link resolveTalentWeaponBonuses}); the `thrown` ones apply here. */
  talentBonuses?: readonly TalentWeaponBonus[];
  /** Equipped systems' and core bonuses' weapon-scoped range rows ({@link resolveScopedRangeBonuses}). */
  scopedRangeBonuses?: readonly ScopedRangeBonus[];
};

/** A weapon mod's profile additions (lancer-data's `added_damage`/`added_range`/`added_tags`). */
type RawMod = { added_damage?: RawWeapon["damage"]; added_range?: RawWeapon["range"]; added_tags?: RawWeapon["tags"] };

/**
 * `weapon` with its slot's mod folded in, for the combat freeze: an added Range or tag the weapon
 * already carries with a number adds to that number (the Stabilizer Mod's "+5 Range", the
 * Supermassive Mod's "KNOCKBACK +1"); anything else is appended, as COMP/CON concatenates them.
 * The mod's alternate tunings (the Supermassive Mod's Full Repair choice) are not modelled.
 */
function withMod(weapon: RawWeapon, mod: RawMod | undefined): RawWeapon {
  if (!mod) return weapon;
  const merge = <T extends { val?: unknown }>(base: T[] | undefined, added: T[] | undefined, same: (a: T, b: T) => boolean): T[] | undefined => {
    if (!added?.length) return base;
    const out = [...(base ?? [])];
    for (const a of added) {
      const i = out.findIndex((b) => same(a, b));
      const n = (v: unknown) => (typeof v === "number" ? v : typeof v === "string" ? Number(v) : NaN);
      if (i >= 0 && Number.isFinite(n(out[i]!.val)) && Number.isFinite(n(a.val))) out[i] = { ...out[i]!, val: n(out[i]!.val) + n(a.val) };
      else if (i < 0) out.push(a);
    }
    return out;
  };
  const damage = mod.added_damage?.length ? [...(weapon.damage ?? []), ...mod.added_damage] : weapon.damage;
  const range = merge(weapon.range, mod.added_range, (a, b) => a.type === b.type);
  const tags = merge(weapon.tags, mod.added_tags, (a, b) => a.id === b.id);
  return { ...weapon, ...(damage ? { damage } : {}), ...(range ? { range } : {}), ...(tags ? { tags } : {}) };
}

export function freezeWeapons(args: FreezeWeaponsArgs): WeaponProfile[] {
  const { mech, pilot, compendium, integratedIds = [], rangeBonus = 0, talentBonuses = [], scopedRangeBonuses = [] } = args;
  const profiles: WeaponProfile[] = [];
  const freezeId = (id: string, instanceId: string, modId?: string | null) => {
    const weapon = compendium.getById("weapon", id) as RawWeapon | undefined;
    const mod = modId ? (compendium.getById("mod", modId) as RawMod | undefined) : undefined;
    if (weapon) profiles.push(applyScopedRangeBonuses(applyThrownBonuses(applyRangeBonus(freezeWeaponProfile(withMod(weapon, mod), id, instanceId), rangeBonus), talentBonuses), scopedRangeBonuses));
    // unresolved id (or an integrated *system*, not a weapon) → skip (can't freeze it)
  };
  // Frame-integrated weapons deploy automatically (the Barbarossa's Apocalypse Rail is
  // a Ship-class integrated weapon — not a normal mount slot, so it never appears in the
  // loadout). Freeze these first so a frame's signature weapon is always armed.
  integratedIds.forEach((id, i) => freezeId(id, `integrated:${i}`));
  const loadout = mech.loadouts[mech.activeLoadoutIndex];
  if (loadout) {
    composedMounts(loadout, pilot, compendium).forEach((mount, mountIndex) => {
      mount.slots.forEach((slot, slotIndex) => {
        if (slot.weaponId) freezeId(slot.weaponId, `${mountIndex}:${slotIndex}`, slot.modId);
      });
    });
  }
  return profiles;
}

/**
 * Freeze the mech's **slot-resolved mounts** — the shared seam System Trauma and Barrage's Aux
 * rider both consume. Walks the active loadout's mounts
 * in order (mirroring {@link freezeWeapons}'s own walk); each {@link FrozenMount.type} is the
 * mount's raw `MountData.type` string verbatim (NOT normalized to a fitting-size enum — see
 * {@link FrozenMount}'s doc for why), and `slots` lists only the OCCUPIED slots (an empty roster
 * slot contributes no entry — the same skip-if-empty discipline {@link freezeWeapons} follows,
 * so a consumer keying off `slots[].weaponInstanceId` never sees a phantom id for an empty slot).
 *
 * **`weaponInstanceId` is the exact id {@link freezeWeapons} mints for that slot**
 * (`${mountIndex}:${slotIndex}`) — this is a cross-reference into `CombatantBase.weapons`, not a
 * second identity namespace. Frame-integrated weapons carry no mount and never appear here.
 *
 * **Walks {@link composedMounts}, the SAME composed array {@link freezeWeapons} walks** — a held
 * bonus mount (the Improved Armament Flex mount) appears here too, at the same composed index
 * `freezeWeapons` used to mint its weapon-instance id, so the cross-reference never breaks.
 * If only one of the two walked the composed array, a bonus-mount weapon would arm in
 * `weapons[]` with no matching `mounts[]` entry (or vice versa) — both MUST stay in lockstep.
 *
 * No mech has no loadout (`mech.loadouts[mech.activeLoadoutIndex]` absent) → `[]`, matching
 * {@link freezeWeapons}'s own empty-loadout behavior.
 */
export function freezeMounts(mech: PilotData["mechs"][number], pilot: PilotData, compendium: Compendium): FrozenMount[] {
  const loadout = mech.loadouts[mech.activeLoadoutIndex];
  if (!loadout) return [];
  return composedMounts(loadout, pilot, compendium).map((mount, mountIndex) => {
    const slots: { weaponInstanceId: string }[] = [];
    mount.slots.forEach((slot, slotIndex) => {
      if (slot.weaponId) slots.push({ weaponInstanceId: `${mountIndex}:${slotIndex}` });
    });
    return { type: mount.type, slots };
  });
}

/**
 * The subset of a compendium `system` entry {@link freezeSystems} reads: its display `name` +
 * its `tags` (`tg_indestructible` marks destruction-immune; the same raw tag-ref shape
 * {@link RawWeapon.tags} carries).
 */
type RawSystem = { name?: unknown; tags?: RawWeapon["tags"] };

/**
 * Freeze a {@link FrozenSystem} for every system equipped in the mech's active loadout, **plus**
 * every talent-integrated system the pilot auto-carries (Walking Armory's Ammo Case) — the
 * systems-only counterpart to {@link freezeWeapons} that makes "destroy a system of your choice"
 * (System Trauma, Core p.80) have a real target set, and makes a passive/no-action system visible
 * to combat state. Walks `loadout.systems`
 * (compendium ids, equip order — mirrors {@link freezeActionIds}'s systems walk), resolving
 * each out of the compendium **once**; an unresolved id is skipped (honest degrade, exactly
 * like an unresolved weapon in {@link freezeWeapons}).
 *
 * **Instance ids** namespace as `system:${i}` for loadout systems — `i` is the system's 0-based
 * position in `loadout.systems[]` — a disjoint namespace from every weapon-instance scheme
 * (mount/slot, `integrated:N`, `pilotgear:N`), so a destroyed-set keyed across both weapons and
 * systems never collides. **Talent-integrated systems namespace separately as `talentsystem:${i}`**
 * (`i` is the item's 0-based position in `talentIntegratedGrants(pilot, compendium).systemIds`) —
 * a THIRD disjoint slot, so an auto-granted Ammo Case can never collide with a loadout-equipped
 * `system:N` id even if both existed on the same mech (they can't today — the manual equip is
 * blocked — but the keying stays defensively disjoint regardless).
 *
 * **Tags** freeze via the SAME {@link freezeTagRefs} helper `freezeWeaponProfile` uses —
 * `tg_indestructible`'s exclusion marker (`tables.ts`'s `isSystemDestroyable`) reads this list.
 */
export function freezeSystems(mech: PilotData["mechs"][number], pilot: PilotData, compendium: Compendium): FrozenSystem[] {
  const systems: FrozenSystem[] = [];
  const resolve = (systemId: string): RawSystem | undefined => compendium.getById("system", systemId) as RawSystem | undefined;

  const loadout = mech.loadouts[mech.activeLoadoutIndex];
  (loadout?.systems ?? []).forEach((systemId, i) => {
    const system = resolve(systemId);
    if (!system || typeof system.name !== "string") return; // unresolved id — honest no-op, like freezeWeapons
    const tags = freezeTagRefs(system.tags);
    systems.push({ id: systemId, instanceId: `system:${i}`, name: system.name, ...(tags.length > 0 ? { tags } : {}) });
  });

  // Talent-integrated systems (Ammo Case I/II/III) — a pure spawn-time derivation, same helper
  // the weapon side threads into `integratedWeaponIds` above. Distinct `talentsystem:N`
  // instance-key slot (see doc above).
  talentIntegratedGrants(pilot, compendium).systemIds.forEach((systemId, i) => {
    const system = resolve(systemId);
    if (!system || typeof system.name !== "string") return; // unresolved id — honest no-op
    const tags = freezeTagRefs(system.tags);
    systems.push({ id: systemId, instanceId: `talentsystem:${i}`, name: system.name, ...(tags.length > 0 ? { tags } : {}) });
  });

  return systems;
}
